import { z } from "zod";

const ALLOWED_URL_SCHEMES = new Set(["http:", "https:", "mailto:"]);

/**
 * Returns the value only when it parses as an absolute URL whose scheme is on
 * the allowlist, otherwise null. Callers render the raw value as inert text on
 * null rather than dropping it, so the reader still sees what was attempted.
 *
 * The parse is deliberately absolute-only: resolving against a base URL would
 * promote `javascript:alert(1)` to a harmless-looking relative path and hide
 * the very values this guards against.
 */
export function getSafeUrl(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  try {
    return ALLOWED_URL_SCHEMES.has(new URL(value).protocol) ? value : null;
  } catch {
    return null;
  }
}

/**
 * The write-side counterpart to `getSafeUrl`, for a field that holds a web
 * address a browser will later put in an `href`.
 *
 * `z.string().url()` only asks whether `new URL()` parses, so it accepts
 * `javascript:`, `data:` and `vbscript:`; a stored one of those becomes script
 * execution in the reader's session. `mailto:` is left out on purpose: this
 * guards profile and link fields, where an address is not a valid value.
 */
export const httpUrlSchema = z
  .string()
  .url()
  .refine(
    (value) => {
      try {
        const { protocol } = new URL(value);
        return protocol === "http:" || protocol === "https:";
      } catch {
        return false;
      }
    },
    { message: "URL must use http or https" },
  );
